/**
 * Age range display formatter.
 *
 * Backend stores ages as a closed numeric range, with `99` acting as the
 * internal "no practical upper limit" sentinel. We hide that sentinel from
 * the UI so users never see awkward strings like "Ages 13–99".
 *
 *   formatAgeRange(13, 99) -> "Ages 13+"
 *   formatAgeRange(18, 99) -> "Ages 18+"
 *   formatAgeRange(13, 17) -> "Ages 13–17"
 *   formatAgeRange(25, 40) -> "Ages 25–40"
 *
 * Defensive: missing/invalid bounds fall back to "Ages 13+" so legacy or
 * partially-loaded data never breaks the render tree.
 *
 * Note: uses an en-dash (–) for bounded ranges to match the rest of the
 * app's typography (e.g. skill range pill).
 */

export const NO_UPPER_LIMIT_AGE = 99;
const DEFAULT_MIN_AGE = 13;

function _safe(n, fallback) {
  return Number.isFinite(n) ? n : fallback;
}

export function formatAgeRange(minAge, maxAge) {
  const min = _safe(minAge, DEFAULT_MIN_AGE);
  const max = _safe(maxAge, NO_UPPER_LIMIT_AGE);
  if (max >= NO_UPPER_LIMIT_AGE) return `Ages ${min}+`;
  return `Ages ${min}–${max}`;
}

/**
 * Lowercase, no "Ages" prefix — for embedding inside a sentence.
 *
 *   formatAgeRangeSentence(13, 99) -> "ages 13+"
 *   formatAgeRangeSentence(13, 17) -> "ages 13–17"
 */
export function formatAgeRangeSentence(minAge, maxAge) {
  const min = _safe(minAge, DEFAULT_MIN_AGE);
  const max = _safe(maxAge, NO_UPPER_LIMIT_AGE);
  if (max >= NO_UPPER_LIMIT_AGE) return `ages ${min}+`;
  return `ages ${min}–${max}`;
}
